import React, { useState, useEffect } from 'react';
import { downloadApplicationDocument } from '../lib/fileUtils';
import { useData } from '../contexts/DataContext';
import { Application, ApplicationDocument } from '../types';
import ApplicationForm from './ApplicationForm';
import { useTenant } from '../contexts/TenantContext';
import ApplicationsOnboardingModal from './ApplicationsOnboardingModal';
import { 
  Edit, 
  Trash2, 
  Calendar, 
  Clock, 
  FileText, 
  Building2, 
  AlertCircle, 
  ChevronDown, 
  ChevronRight, 
  Maximize2, 
  Minimize2, 
  Eye, 
  Download, 
  Award, 
  DollarSign, 
  ExternalLink 
} from 'lucide-react';

const ApplicationsKanban: React.FC = () => {
  const { 
    applications, 
    applicationStatuses, 
    tenders, 
    updateApplication
  } = useData();
  const { currentCompany, currentCompanyUser } = useTenant();
  
  const [showForm, setShowForm] = useState(false);
  const [editingApplication, setEditingApplication] = useState<Application | null>(null);
  const [draggedItem, setDraggedItem] = useState<Application | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedApplicationNotes, setSelectedApplicationNotes] = useState<string>('');
  const [maximizedColumn, setMaximizedColumn] = useState<string | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedApplicationDetail, setSelectedApplicationDetail] = useState<Application | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'adjudication'>('details');
  const [showApplicationsOnboarding, setShowApplicationsOnboarding] = useState(false);

  // Verificar si debe mostrar el onboarding de postulaciones
  useEffect(() => {
    console.log('Current company user:', currentCompanyUser);
    if (currentCompanyUser && !currentCompanyUser.Onboarding3) {
      console.log('Mostrando onboarding de postulaciones');
      setShowApplicationsOnboarding(true);
    }
  }, [currentCompanyUser]);

  // Filter applications by current company
  const companyApplications = applications.filter(app => app.company_id === currentCompany?.id);

  // Definir los estados que queremos mostrar en el tablero Kanban en el orden específico
  const statusOrder = ['En Preparacion', 'Ofertada', 'Descartada', 'Adjudicada', 'No Adjudicada'];
  const displayStatuses = statusOrder.map(statusName => 
    applicationStatuses.find(status => status.name === statusName)
  ).filter((status): status is NonNullable<typeof status> => status !== undefined);

  // Función para obtener aplicaciones por estado
  const getApplicationsByStatus = (statusId: string) => {
    return companyApplications.filter(app => app.status_id === statusId);
  };

  // Función para obtener información de la licitación
  const getTenderInfo = (tenderId: string) => {
    return tenders.find(t => t.id === tenderId);
  };

  // Función para formatear fechas
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para formatear monedas
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency === 'CLP' ? 'CLP' : currency === 'USD' ? 'USD' : 'EUR'
    }).format(amount);
  };

  // Función para formatear tamaño de archivo
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Función para manejar el drag start
  const handleDragStart = (e: React.DragEvent, application: Application) => {
    setDraggedItem(application);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Función para manejar el drag over
  const handleDragOver = (e: React.DragEvent, statusId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(statusId);
  };

  // Función para manejar el drag leave
  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  // Función para manejar el drop
  const handleDrop = async (e: React.DragEvent, targetStatusId: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedItem) return;

    // Verificar si el cambio de estado es válido
    const currentStatus = applicationStatuses.find(s => s.id === draggedItem.status_id);
    const targetStatus = applicationStatuses.find(s => s.id === targetStatusId);

    if (!currentStatus || !targetStatus) return;

    // Reglas de transición de estado
    const validTransitions: { [key: string]: string[] } = {
      'En Preparacion': ['Ofertada'],
      'Ofertada': ['Descartada'],
      'Descartada': [],
      'Adjudicada': [], // Solo se cambia automáticamente por el sistema
      'No Adjudicada': [] // Solo se cambia automáticamente por el sistema
    };

    if (!validTransitions[currentStatus.name]?.includes(targetStatus.name)) {
      let errorMessage = `No se puede cambiar de "${currentStatus.name}" a "${targetStatus.name}".`;
      
      if (targetStatus.name === 'Adjudicada' || targetStatus.name === 'No Adjudicada') {
        errorMessage += ` Los estados "${targetStatus.name}" solo se asignan automáticamente por el sistema.`;
      } else if (validTransitions[currentStatus.name]?.length === 0) {
        errorMessage += ` El estado "${currentStatus.name}" es final y no puede cambiarse.`;
      } else {
        errorMessage += ` Transiciones válidas: ${validTransitions[currentStatus.name]?.join(', ') || 'ninguna'}.`;
      }
      
      setErrorMessage(errorMessage);
      return;
    }

    try {
      await updateApplication(draggedItem.id, { status_id: targetStatusId });
      setErrorMessage(null);
    } catch (error) {
      console.error('Error updating application:', error);
      setErrorMessage('Error al actualizar el estado de la postulación');
    }

    setDraggedItem(null);
  };

  // Función para abrir el modal de detalle
  const handleOpenDetailModal = (application: Application) => {
    setSelectedApplicationDetail(application);
    setActiveTab('details'); // Reset to details tab
    setShowDetailModal(true);
  };

  // Función para manejar la edición
  const handleEdit = (application: Application) => {
    setEditingApplication(application);
    setShowForm(true);
  };

  // Función para manejar el descarte de postulación
  const handleDiscardApplication = async (application: Application) => {
    if (window.confirm('¿Estás seguro de que quieres descartar esta postulación?')) {
      try {
        // Cambiar el estado a "Descartada"
        const discardedStatus = applicationStatuses.find(s => s.name === 'Descartada');
        if (discardedStatus) {
          await updateApplication(application.id, { status_id: discardedStatus.id });
        }
      } catch (error) {
        console.error('Error discarding application:', error);
        setErrorMessage('Error al descartar la postulación');
      }
    }
  };

  // Función para manejar el cierre del formulario
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingApplication(null);
  };


  // Función para manejar las notas
  const handleNotesClick = (application: Application) => {
    setSelectedApplicationNotes(application.notes || '');
    setShowNotesModal(true);
  };

  // Función para guardar las notas
  const handleSaveNotes = async () => {
    if (!selectedApplicationDetail) return;
    try {
      await updateApplication(selectedApplicationDetail.id, { notes: selectedApplicationNotes });
      setShowNotesModal(false);
      setSelectedApplicationNotes('');
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  // Función para alternar la maximización de columna
  const toggleMaximizeColumn = (statusId: string) => {
    if (maximizedColumn === statusId) {
      setMaximizedColumn(null);
      } else {
      setMaximizedColumn(statusId);
    }
  };

  // Función para alternar el colapso de columna
  const toggleCollapseColumn = (statusId: string) => {
    const newCollapsedColumns = new Set(collapsedColumns);
    if (collapsedColumns.has(statusId)) {
      newCollapsedColumns.delete(statusId);
      } else {
      newCollapsedColumns.add(statusId);
      }
    setCollapsedColumns(newCollapsedColumns);
  };

  if (!currentCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Selecciona una empresa para ver las postulaciones</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tablero de Postulaciones</h1>
        <p className="text-gray-600">Gestiona el flujo de postulaciones de {currentCompany?.name}</p>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{errorMessage}</p>
            <p className="text-xs text-red-700 mt-1">
              Los estados "Adjudicada" y "No Adjudicada" solo se asignan automáticamente por el sistema.
            </p>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className={`flex gap-8 overflow-x-auto pb-4 min-h-[800px] ${
          maximizedColumn ? 'justify-center' : ''
        }`}>
          {displayStatuses.map((status) => {
            // Si hay una columna maximizada, solo mostrar esa columna
            if (maximizedColumn && maximizedColumn !== status.id) {
              return null;
            }

            let statusApplications = getApplicationsByStatus(status.id);
            // Ordenar solo si es la columna En Revision
            if (status.name === 'En Preparacion') {
              statusApplications = statusApplications.slice().sort((a, b) => {
                const tenderA = getTenderInfo(a.tender_id);
                const tenderB = getTenderInfo(b.tender_id);
                if (!tenderA || !tenderB) return 0;
                return new Date(tenderA.closing_date).getTime() - new Date(tenderB.closing_date).getTime();
              });
            }
            const isDragOver = dragOverColumn === status.id;
            
            return (
              <div 
                key={status.id} 
                className={`flex-shrink-0 w-80 ${
                  isDragOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : ''
                } ${collapsedColumns.has(status.id) ? 'w-16' : ''}`}
                onDragOver={(e) => handleDragOver(e, status.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status.id)}
              >
                <div className="bg-gray-50 rounded-lg p-4 h-full">
                  {/* Header de la columna */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: status.color }}
                      />
                      <h3 className="font-semibold text-gray-900">{status.name}</h3>
                      <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
                        {statusApplications.length}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => toggleCollapseColumn(status.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                        title={collapsedColumns.has(status.id) ? 'Expandir' : 'Colapsar'}
                      >
                        {collapsedColumns.has(status.id) ? (
                          <ChevronRight className="h-4 w-4 text-gray-600" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                      <button
                        onClick={() => toggleMaximizeColumn(status.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                        title={maximizedColumn === status.id ? 'Restaurar' : 'Maximizar'}
                      >
                        {maximizedColumn === status.id ? (
                          <Minimize2 className="h-4 w-4 text-gray-600" />
                        ) : (
                          <Maximize2 className="h-4 w-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Lista de aplicaciones */}
                  {!collapsedColumns.has(status.id) && (
                    <div className="space-y-3">
                      {statusApplications.map((application) => {
                        const tender = getTenderInfo(application.tender_id);
                        return (
                          <div 
                            key={application.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, application)}
                            className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-move"
                          >
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                                  {tender?.title || 'Licitación no encontrada'}
                                </h4>
                              <div className="flex items-center space-x-1 ml-2">
                                <button
                                  onClick={() => handleOpenDetailModal(application)}
                                  className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                  title="Ver detalles"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                {(() => {
                                  const tender = getTenderInfo(application.tender_id);
                                  if (tender && tender.tender_code) {
                                    return (
                  <a
                    href={`https://www.mercadopublico.cl/fichaLicitacion.html?idLicitacion=${tender.tender_code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 hover:bg-gray-100 rounded text-green-600"
                    title="Ver ficha en Mercado Público"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                                    );
                                  }
                                  return null;
                                })()}
                                <button
                                  onClick={() => handleEdit(application)}
                                  className="p-1 hover:bg-gray-100 rounded text-gray-600"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDiscardApplication(application)}
                                  className={`p-1 rounded ${
                                    applicationStatuses.find(s => s.id === application.status_id)?.name === 'En Preparacion'
                                      ? 'hover:bg-gray-100 text-red-600'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                  title={
                                    applicationStatuses.find(s => s.id === application.status_id)?.name === 'En Preparacion'
                                      ? 'Descartar Postulación'
                                      : 'Solo se puede descartar en estado En Preparacion'
                                  }
                                  disabled={applicationStatuses.find(s => s.id === application.status_id)?.name !== 'En Preparacion'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              </div>

                            <div className="space-y-2">
                              {/* Propuesta */}
                              <div className="flex items-center text-xs text-green-600 font-semibold">
                                <DollarSign className="h-3 w-3 mr-1" />
                                <span>Propuesta: {formatCurrency(application.amount, application.currency)}</span>
                                  </div>
                              
                              {/* Presupuesto */}
                                {tender && (
                                <div className="flex items-center text-xs text-orange-600 font-semibold">
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  <span>Presupuesto: {formatCurrency(tender.amount || 0, tender.currency)}</span>
                                  </div>
                                )}

                              {/* Duración */}
                              {tender?.contract_months && (
                                <div className="flex items-center text-xs text-gray-600">
                                  <Clock className="h-3 w-3 mr-1" />
                                  <span>Duración: {tender.contract_months} meses</span>
                                </div>
                              )}

                              {/* Fecha de envío */}
                              <div className="flex items-center text-xs text-gray-600">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  <span>Enviado: {formatDate(application.submission_date)}</span>
                                </div>
                              
                              {/* Fecha de cierre y días faltantes */}
                                {tender && (
                                <div className="space-y-1">
                                  <div className="flex items-center text-xs text-gray-600">
                                    <Clock className="h-3 w-3 mr-1" />
                                      <span>Cierre: {formatDate(tender.closing_date)}</span>
                                    </div>
                                  {tender.closing_date && (
                                    <div className="flex justify-end">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                        <Clock className="h-3 w-3 mr-1" />
                                        {(() => {
                                          const closingDate = new Date(tender.closing_date);
                                          const now = new Date();
                                          const diffTime = closingDate.getTime() - now.getTime();
                                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                          
                                          if (diffDays > 0) {
                                            return `${diffDays} días`;
                                          } else if (diffDays === 0) {
                                            return 'Hoy';
                                          } else {
                                            return 'Vencida';
                                          }
                                        })()}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              
                              {/* Código de licitación */}
                              {tender?.tender_code && (
                                <div className="flex items-center text-xs text-purple-600 font-mono">
                                  <span className="truncate">{tender.tender_code}</span>
                                </div>
                              )}
                            </div>
                            
                                  {application.notes && (
                              <div className="mt-2">
                                    <button
                                  onClick={() => handleNotesClick(application)}
                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  Ver notas
                                    </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
        
      {/* Formulario de aplicación */}
      {showForm && (
        <ApplicationForm
          onClose={handleCloseForm}
          application={editingApplication}
        />
      )}

      {/* Modal de notas */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Notas de la Postulación</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={selectedApplicationNotes}
                onChange={(e) => setSelectedApplicationNotes(e.target.value)}
                className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Agrega notas sobre esta postulación..."
              />
              </div>
            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 mr-3"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveNotes}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedApplicationDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Eye className="h-5 w-5 text-blue-500 mr-2" />
                Detalle de la Postulación
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-hidden">
              {/* Pestañas */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="h-4 w-4 inline mr-2" />
                    Detalles de la Postulación
                  </button>
                  {(selectedApplicationDetail.adjudication_metadata || selectedApplicationDetail.provider_metadata) && (
                    <button
                      onClick={() => setActiveTab('adjudication')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'adjudication'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Award className="h-4 w-4 inline mr-2" />
                      Información de Adjudicación
                    </button>
                  )}
                </nav>
              </div>

              {/* Contenido de las pestañas */}
              <div className="p-6 overflow-y-auto max-h-[80vh]">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Grid principal con 3 columnas */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Columna 1: Información de la Postulación */}
                <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-blue-500" />
                          Información de la Postulación
                        </h4>
                  
                        <div className="space-y-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm font-medium text-gray-600">Estado:</span>
                            <div className="flex items-center mt-2">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: applicationStatuses.find(s => s.id === selectedApplicationDetail.status_id)?.color || '#6b7280' }}
                        />
                              <span className="text-sm font-semibold text-gray-900">
                          {applicationStatuses.find(s => s.id === selectedApplicationDetail.status_id)?.name || 'No especificado'}
                        </span>
                      </div>
                    </div>
                    
                          <div className="bg-green-50 p-3 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Propuesta:</span>
                            <p className="text-xl font-bold text-green-700 mt-1">
                        {formatCurrency(selectedApplicationDetail.amount, selectedApplicationDetail.currency)}
                      </p>
                    </div>
                    
                          <div className="bg-blue-50 p-3 rounded-lg">
                      <span className="text-sm font-medium text-gray-600">Fecha de Envío:</span>
                            <p className="text-sm font-semibold text-blue-700 mt-1">
                        {formatDate(selectedApplicationDetail.submission_date)}
                      </p>
                    </div>
                    
                    {selectedApplicationDetail.notes && (
                            <div className="bg-yellow-50 p-3 rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Notas:</span>
                              <p className="text-sm text-yellow-800 mt-1">{selectedApplicationDetail.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                      {/* Columna 2: Información de la Licitación */}
                <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-green-500" />
                          Información de la Licitación
                        </h4>
                  
                  {(() => {
                          const tender = tenders.find(t => t.id === selectedApplicationDetail.tender_id);
                          if (!tender) {
                            return (
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-600">No se encontró información de la licitación</p>
                              </div>
                            );
                          }
                    
                    return (
                            <div className="space-y-4">
                              <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-sm font-medium text-gray-600">Título:</span>
                                <p className="text-sm font-semibold text-gray-900 mt-1">{tender.title}</p>
                        </div>
                        
                              <div className="bg-orange-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Presupuesto:</span>
                                <p className="text-xl font-bold text-orange-700 mt-1">
                                  {formatCurrency(tender.amount || 0, tender.currency)}
                          </p>
                        </div>
                        
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Fecha de Cierre:</span>
                                <p className="text-sm font-semibold text-blue-700 mt-1">
                                  {formatDate(tender.closing_date)}
                                </p>
                                {tender.closing_date && (
                                  <div className="mt-2">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {(() => {
                                        const closingDate = new Date(tender.closing_date);
                                        const now = new Date();
                                        const diffTime = closingDate.getTime() - now.getTime();
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        
                                        if (diffDays > 0) {
                                          return `${diffDays} días`;
                                        } else if (diffDays === 0) {
                                          return 'Hoy';
                                        } else {
                                          return 'Vencida';
                                        }
                                      })()}
                                    </span>
                                  </div>
                                )}
                        </div>
                        
                        {tender.contract_months && (
                                <div className="bg-teal-50 p-3 rounded-lg">
                                  <span className="text-sm font-medium text-gray-600">Duración:</span>
                                  <p className="text-sm font-semibold text-teal-700 mt-1 flex items-center">
                                    <Clock className="h-4 w-4 mr-1" />
                                    {tender.contract_months} meses
                            </p>
                          </div>
                        )}
                        
                        {tender.tender_code && (
                                <div className="bg-purple-50 p-3 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Código de Licitación:</span>
                                  <p className="text-sm font-mono font-semibold text-purple-700 mt-1">{tender.tender_code}</p>
                                </div>
                              )}
                              
                              {tender.description && (
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <span className="text-sm font-medium text-gray-600">Descripción:</span>
                                  <p className="text-sm text-gray-800 mt-1 line-clamp-3">{tender.description}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                      {/* Columna 3: Información de la Empresa */}
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-2 flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-purple-500" />
                          Información de la Empresa
                        </h4>
                        
                        {currentCompany && (
                          <div className="space-y-4">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <span className="text-sm font-medium text-gray-600">Empresa:</span>
                              <p className="text-sm font-semibold text-gray-900 mt-1">{currentCompany.name}</p>
              </div>

                            {currentCompany.rut && (
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">RUT:</span>
                                <p className="text-sm font-mono text-blue-700 mt-1">{currentCompany.rut}</p>
                              </div>
                            )}
                            
                            {currentCompany.address && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Dirección:</span>
                                <p className="text-sm text-green-700 mt-1">{currentCompany.address}</p>
                              </div>
                            )}
                            
                            {currentCompany.email && (
                              <div className="bg-purple-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Email de Contacto:</span>
                                <p className="text-sm text-purple-700 mt-1">{currentCompany.email}</p>
                              </div>
                            )}
                            
                            {currentCompany.contact_phone && (
                              <div className="bg-teal-50 p-3 rounded-lg">
                                <span className="text-sm font-medium text-gray-600">Teléfono de Contacto:</span>
                                <p className="text-sm text-teal-700 mt-1">{currentCompany.contact_phone}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Sección de Documentos */}
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2 text-indigo-500" />
                        Documentos de la Postulación
                      </h4>
                {selectedApplicationDetail.documents_metadata && selectedApplicationDetail.documents_metadata.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedApplicationDetail.documents_metadata.map((doc: ApplicationDocument, index: number) => (
                            <div key={index} className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                                  <h5 className="text-sm font-semibold text-gray-900 mb-1">{doc.title}</h5>
                                  <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                                    <span className="bg-gray-100 px-2 py-1 rounded">{doc.type}</span>
                              {doc.file_size && (
                                      <span className="bg-gray-100 px-2 py-1 rounded">{formatFileSize(doc.file_size)}</span>
                              )}
                            </div>
                          </div>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mb-3">
                            {doc.required && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                                Requerido
                              </span>
                            )}
                                {doc.signed && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                Firmado
                              </span>
                                )}
                                {!doc.signed && doc.required && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                    Pendiente de Firma
                              </span>
                            )}
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="text-xs text-gray-500">
                                  {doc.uploaded_at && (
                                    <span>Subido: {formatDate(doc.uploaded_at)}</span>
                                  )}
                                </div>
                                <div className="flex space-x-2">
                                  {doc.file_name && (
                            <button
                                      onClick={async () => {
                                        try {
                                          await downloadApplicationDocument(doc);
                                        } catch (error) {
                                          console.error('Error downloading document:', error);
                                        }
                                      }}
                                      className="text-blue-600 hover:text-blue-800 text-xs font-medium flex items-center"
                                    >
                                      <Download className="h-3 w-3 mr-1" />
                                      Descargar
                            </button>
                                  )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg">
                          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg font-medium mb-2">No hay documentos asociados</p>
                          <p className="text-sm">Los documentos aparecerán aquí cuando se suban a la postulación</p>
                  </div>
                )}
              </div>
            </div>
                )}

                {activeTab === 'adjudication' && (
                  <div className="space-y-6">
                    {/* Información de adjudicación */}
                    {selectedApplicationDetail.adjudication_metadata && selectedApplicationDetail.adjudication_metadata.UrlActa && (
                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-blue-600" />
                          Acta de Adjudicación
                        </h4>
                        <div className="bg-indigo-50 p-4 rounded-lg">
                          <span className="text-sm font-medium text-gray-600">Documento Oficial:</span>
                          <a 
                            href={selectedApplicationDetail.adjudication_metadata.UrlActa}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-700 mt-2 underline hover:text-indigo-900 block"
                          >
                            Ver acta oficial →
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {/* Información de los proveedores adjudicados */}
                    {selectedApplicationDetail.provider_metadata && (
                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                          <Award className="h-5 w-5 mr-2 text-blue-600" />
                          Proveedores Adjudicados
                        </h4>
                        
                        {selectedApplicationDetail.provider_metadata.Listado && 
                         selectedApplicationDetail.provider_metadata.Listado.length > 0 && (
                          <div className="space-y-4">
                            {selectedApplicationDetail.provider_metadata.Listado.map((item, index) => (
                              item.Adjudicacion && (
                                <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                                  <div className="flex items-center mb-3">
                                    <Award className="h-4 w-4 text-green-600 mr-2" />
                                    <span className="text-sm font-medium text-gray-700">Proveedor Adjudicado</span>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {item.Adjudicacion.NombreProveedor && (
                                      <div className="bg-green-50 p-3 rounded-lg">
                                        <span className="text-sm font-medium text-gray-600">Empresa:</span>
                                        <p className="text-sm text-green-700 font-semibold mt-1">
                                          {item.Adjudicacion.NombreProveedor}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {item.Adjudicacion.RutProveedor && (
                                      <div className="bg-gray-50 p-3 rounded-lg">
                                        <span className="text-sm font-medium text-gray-600">RUT:</span>
                                        <p className="text-sm text-gray-900 font-mono mt-1">
                                          {item.Adjudicacion.RutProveedor}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {item.Adjudicacion.Cantidad && (
                                      <div className="bg-blue-50 p-3 rounded-lg">
                                        <span className="text-sm font-medium text-gray-600">Cantidad:</span>
                                        <p className="text-sm text-blue-700 font-semibold mt-1">
                                          {item.Adjudicacion.Cantidad} unidades
                                        </p>
                                      </div>
                                    )}
                                    
                                    {item.Adjudicacion.MontoUnitario && (
                                      <div className="bg-purple-50 p-3 rounded-lg">
                                        <span className="text-sm font-medium text-gray-600">Monto Unitario:</span>
                                        <p className="text-sm text-purple-700 font-semibold mt-1">
                                          {formatCurrency(item.Adjudicacion.MontoUnitario, selectedApplicationDetail.currency)}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {item.Adjudicacion.Cantidad && item.Adjudicacion.MontoUnitario && (
                                      <div className="bg-green-50 p-3 rounded-lg md:col-span-2">
                                        <span className="text-sm font-medium text-gray-600">Total Adjudicado:</span>
                                        <p className="text-lg font-bold text-green-700 mt-1">
                                          {formatCurrency(
                                            item.Adjudicacion.Cantidad * item.Adjudicacion.MontoUnitario, 
                                            selectedApplicationDetail.currency
                                          )}
                                        </p>
                                        <p className="text-xs text-green-600 mt-1">
                                          ({item.Adjudicacion.Cantidad} × {formatCurrency(item.Adjudicacion.MontoUnitario, selectedApplicationDetail.currency)})
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de onboarding de postulaciones */}
      <ApplicationsOnboardingModal
        isOpen={showApplicationsOnboarding}
        onClose={() => setShowApplicationsOnboarding(false)}
        onComplete={() => setShowApplicationsOnboarding(false)}
      />
    </div>
  );
};

export default ApplicationsKanban;